import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Card from '@mui/material/Card';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTranslate } from 'src/locales';
import { PageOptions, SortConfig } from 'src/services/api.types';
import { SortOrder } from 'src/services/common.types';
import { useGetOrganizationContactsLeanQuery, useRemoveContactMutation } from 'src/services/contacts/contacts.service';
import { IContactListItem } from 'src/services/contacts/contacts.types';
import { EmailMessageTemplateType, MailerTargetType } from 'src/services/email-sync/email-sync.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { openMultiMailerDialog, openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import DataListActionsMenu from 'src/components/data-list/data-list-actions-menu';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';

import AddContactModal from 'src/sections/contacts/modal/add-contact-modal';
import AddItemsModal from 'src/sections/lists/modals/add-items-modal';

import { buildContactColumns } from './contact-columns';
import ContactRowActions from './contact-row-actions';

const PAGE_SIZE = 50;

/**
 * Contacts list on the shared DataList (JT1-1527/1528, item 5). Configurable/sortable columns from
 * the lean endpoint, per-column search, infinite scroll, and the contact actions (view, notes,
 * email, call/SMS, add task, remove) via ContactRowActions. Replaces the CONTACTS tab's old table.
 */
export default function ContactsList() {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const authContext = useAuthContext();
  const orgId = authContext.organization?.id;

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<IContactListItem[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'last_activity_at', sort_order: SortOrder.DESC });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const pageOptions: PageOptions = useMemo(() => ({ page, per_page: PAGE_SIZE }), [page]);

  const { currentData, isFetching } = useGetOrganizationContactsLeanQuery(
    { org_id: orgId as string, search_query: filterQuery || undefined, column_filters: columnSearch, page: pageOptions, sort_config: sortConfig },
    { skip: !orgId, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!currentData) return;
    setRows((prev) => {
      if (page === 1) return currentData.results;
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...currentData.results.filter((r) => !seen.has(r.id))];
    });
  }, [currentData, page]);

  const count = currentData?.count ?? 0;
  const hasMore = rows.length < count;
  const isSearching = isFetching || searchInput !== filterQuery;

  const resetToFirstPage = useCallback(() => setPage(1), []);

  const applySearch = useMemo(() => debounce((value: string) => { setFilterQuery(value); resetToFirstPage(); }, 300), [resetToFirstPage]);
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const onSearchChange = useCallback((value: string) => { setSearchInput(value); applySearch(value); }, [applySearch]);
  const onSortChange = useCallback((next: SortConfig) => { setSortConfig(next); resetToFirstPage(); }, [resetToFirstPage]);
  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    resetToFirstPage();
  }, [resetToFirstPage]);
  const onLoadMore = useCallback(() => { if (!isFetching && hasMore) setPage((p) => p + 1); }, [isFetching, hasMore]);

  const onSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const onSelectAll = useCallback((checked: boolean, ids: string[]) => setSelectedIds(checked ? Array.from(new Set(ids)) : []), []);

  const addContact = useBoolean();
  const addToList = useBoolean();

  const [removeContact, { isLoading: isRemoving }] = useRemoveContactMutation();

  const handleRemove = useCallback(async (row: IContactListItem) => {
    try {
      await removeContact({ contact_id: row.id }).unwrap();
      enqueueSnackbar(t('contacts.api.delete.success'), { variant: 'success' });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch {
      enqueueSnackbar(t('contacts.api.delete.default_error'), { variant: 'error' });
    }
  }, [removeContact, t]);

  const handleSendEmail = useCallback((row: IContactListItem) => {
    if (!row.email) return;
    dispatch(openSingleMailerDialog({
      emails: [row.email],
      templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      templateEntityId: row.id,
      targetType: MailerTargetType.CONTACT,
      targetId: row.id,
    }));
  }, [dispatch]);

  const handleBulkEmail = useCallback(() => {
    if (!selectedIds.length) return;
    dispatch(openMultiMailerDialog({
      targetType: MailerTargetType.CONTACT,
      templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      entities: selectedIds,
    }));
  }, [dispatch, selectedIds]);

  const onSuccessAddToList = useCallback(() => { setSelectedIds([]); addToList.onFalse(); }, [addToList]);

  const registry = useMemo(() => buildContactColumns({ t }), [t]);
  const { columns, pickerModel, save } = useColumnPreferences('contacts', registry);

  const renderRowActions = useCallback((row: IContactListItem) => (
    <ContactRowActions row={row} onSendEmail={handleSendEmail} onRemove={handleRemove} isRemoving={isRemoving} />
  ), [handleSendEmail, handleRemove, isRemoving]);

  const hasSelection = selectedIds.length > 0;

  const toolbar = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <TextField
        size="small"
        value={searchInput}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('contacts.table.search_placeholder', { defaultValue: 'Search contacts' })}
        InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
        sx={{ width: 260 }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <DataListColumnPicker model={pickerModel} onSave={save} />
        <DataListActionsMenu
          actions={[
            { key: 'add-contact', label: t('contacts.add_contact', { defaultValue: 'Add contact' }), icon: 'mingcute:add-line', onClick: addContact.onTrue },
            { key: 'add-to-list', label: t('common.add_to_list', { defaultValue: 'Add to list' }), icon: 'solar:list-bold', onClick: addToList.onTrue, disabled: !hasSelection },
            { key: 'send-email', label: t('common.send_email'), icon: 'lsicon:email-send-filled', onClick: handleBulkEmail, disabled: !hasSelection },
          ]}
        />
      </Stack>
    </Stack>
  );

  return (
    <>
      <Card sx={{ borderRadius: 0 }}>
        <DataList<IContactListItem>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isSearching={isSearching}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          sort={sortConfig}
          onSortChange={onSortChange}
          columnSearch={columnSearch}
          onColumnSearchChange={onColumnSearchChange}
          toolbar={toolbar}
          selectable
          selectedIds={selectedIds}
          onSelectRow={onSelectRow}
          onSelectAll={onSelectAll}
          renderRowActions={renderRowActions}
        />
      </Card>

      <AddContactModal open={addContact.value} onClose={addContact.onFalse} />
      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={onSuccessAddToList} items={selectedIds} entityType={ListEntityType.CONTACT} />
    </>
  );
}
